/**
 * GENERATED by scripts/bake-env.mjs — do not edit. Re-run the script instead.
 *
 * 5 per-location environment probes, 512x256, 10 mips, rgba16float.
 * The shader picks two by world Y and blends, so what a surface reflects changes
 * as it descends. See the script for the radiance model and for how to swap it
 * for a real HDR capture.
 */

export const ENV_W = 512;
export const ENV_H = 256;
export const ENV_MIPS = 10;
export const ENV_LAYERS = 5;
export const ENV_PEAK = 3.976753;

/** World Y each probe is centred on, ASCENDING — so index 0 is the deepest. */
export const ENV_BANDS: readonly number[] = [-1200, -450, -150, -45, -6];
export const ENV_NAMES: readonly string[] = ['abyss', 'trench', 'cave', 'shelf', 'surface'];

/** Irradiance per probe, nine coefficients each, deepest first (matches ENV_BANDS). */
export const ENV_SH_ALL: readonly (readonly (readonly number[])[])[] = [
  [
    [0.006161, 0.012000, 0.022155],
    [0.002723, 0.003622, 0.005724],
    [0.000000, 0.000000, 0.000000],
    [0.000000, -0.000000, 0.000000],
    [0.000000, 0.000000, -0.000000],
    [0.000000, 0.000000, 0.000000],
    [-0.000674, -0.001086, -0.001695],
    [0.000000, 0.000000, -0.000000],
    [-0.001167, -0.001881, -0.002936],
  ],
  [
    [0.034980, 0.064726, 0.096724],
    [0.020824, 0.026648, 0.034600],
    [0.000000, 0.000000, 0.000000],
    [-0.000000, -0.000000, 0.000000],
    [-0.000000, -0.000000, -0.000000],
    [0.000000, 0.000000, 0.000000],
    [-0.004321, -0.006545, -0.008490],
    [-0.000000, -0.000000, 0.000000],
    [-0.007484, -0.011335, -0.014704],
  ],
  [
    [0.191105, 0.310013, 0.387934],
    [0.121501, 0.153602, 0.178584],
    [0.000000, 0.000000, 0.000000],
    [0.000000, 0.000000, 0.000000],
    [0.000000, 0.000000, -0.000000],
    [0.000000, 0.000000, 0.000000],
    [-0.024812, -0.034447, -0.039396],
    [0.000000, -0.000000, 0.000000],
    [-0.042976, -0.059665, -0.068235],
  ],
  [
    [0.811810, 1.220887, 1.369134],
    [0.545738, 0.678474, 0.752336],
    [0.000000, 0.000000, 0.000000],
    [0.000000, 0.000000, -0.000000],
    [-0.000000, 0.000000, -0.000000],
    [0.000000, 0.000000, 0.000000],
    [-0.107532, -0.142973, -0.153234],
    [-0.000000, -0.000000, 0.000000],
    [-0.186251, -0.247636, -0.265408],
  ],
  [
    [1.832988, 2.589238, 2.793527],
    [1.240471, 1.516695, 1.653174],
    [0.000000, 0.000000, 0.000000],
    [-0.000000, -0.000000, -0.000000],
    [-0.000000, 0.000000, 0.000000],
    [0.000000, 0.000000, 0.000000],
    [-0.241410, -0.311129, -0.326660],
    [0.000000, -0.000000, 0.000000],
    [-0.418133, -0.538891, -0.565791],
  ],
];

/** Back-compat: the shallowest probe's irradiance, for anything still reading one. */
export const ENV_SH = ENV_SH_ALL[ENV_SH_ALL.length - 1]!;



/** Bytes one full probe occupies, mips included. */
export const ENV_LAYER_BYTES = (() => {
  let n = 0;
  for (let m = 0; m < ENV_MIPS; m++) {
    n += Math.max(1, ENV_W >> m) * Math.max(1, ENV_H >> m) * 8;
  }
  return n;
})();

/** Total size of the blob, for validating a short or truncated fetch. */
export const ENV_BYTES = ENV_LAYER_BYTES * ENV_LAYERS;

/** Where the baked blob lives. Fetched at boot — see main.ts. */
export const ENV_URL = '/env/probes-db72a1e0.bin';

/**
 * Upload every probe as one 2D array texture with a full mip chain.
 *
 * data is the fetched blob, or null when the fetch failed. A null builds the
 * texture from the spherical harmonics instead, which are inlined and tiny:
 * every mip of every layer gets that probe's band-0 irradiance as a flat
 * colour. Reflections go featureless, which is what they were before the probes
 * existed, and nothing crashes or renders black — the same posture
 * loadDetailTextures takes when the art is missing.
 */
export function buildEnvTexture(device: GPUDevice, data: ArrayBuffer | null): GPUTexture {
  const ok = data !== null && data.byteLength >= ENV_BYTES;
  if (data !== null && !ok) {
    console.warn(`[abyss] env probes truncated: ${data.byteLength} of ${ENV_BYTES} bytes`);
  }
  // Typed as ArrayBuffer, not ArrayBufferLike: WebGPU's writeTexture will not
  // take a view that might be backed by a SharedArrayBuffer, and a fetched
  // blob never is.
  const bin: Uint8Array<ArrayBuffer> = ok
    ? new Uint8Array(data as ArrayBuffer) : fallbackBlob();
  const tex = device.createTexture({
    size: [ENV_W, ENV_H, ENV_LAYERS],
    format: 'rgba16float',
    mipLevelCount: ENV_MIPS,
    dimension: '2d',
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
    label: 'envProbes',
  });
  let off = 0;
  // Baked shallow-first, stored deepest-first, so layer 0 lines up with ENV_BANDS[0].
  for (let src = ENV_LAYERS - 1; src >= 0; src--) {
    let o = 0;
    for (let k = 0; k < src; k++) {
      for (let m = 0; m < ENV_MIPS; m++) {
        o += Math.max(1, ENV_W >> m) * Math.max(1, ENV_H >> m) * 8;
      }
    }
    const layer = ENV_LAYERS - 1 - src;
    for (let m = 0; m < ENV_MIPS; m++) {
      const w = Math.max(1, ENV_W >> m), h = Math.max(1, ENV_H >> m);
      const n = w * h * 8;
      device.queue.writeTexture(
        { texture: tex, mipLevel: m, origin: { x: 0, y: 0, z: layer } },
        bin.subarray(o, o + n),
        { bytesPerRow: w * 8, rowsPerImage: h },
        { width: w, height: h, depthOrArrayLayers: 1 },
      );
      o += n;
    }
  }
  void off;
  return tex;
}

/**
 * A flat probe per layer, from the inlined SH, for when the blob is missing.
 *
 * Band 0 alone — sh[0] * 0.282095 is the constant term of the irradiance, so
 * this is the average colour of that depth's environment. Laid into the same
 * mip-major layout the uploader walks, so nothing downstream knows the
 * difference.
 */
function fallbackBlob(): Uint8Array<ArrayBuffer> {
  const bin = new Uint8Array(new ArrayBuffer(ENV_BYTES));
  const u16 = new Uint16Array(bin.buffer);
  // Written in the blob's own order, which is shallow-first.
  for (let src = 0; src < ENV_LAYERS; src++) {
    const sh = ENV_SH_ALL[ENV_LAYERS - 1 - src]!;
    const c = [0, 1, 2].map((k) => Math.max(0, sh[0]![k]! * 0.282095));
    const h = c.map(halfBits);
    let o = (src * ENV_LAYER_BYTES) >> 1;
    for (let m = 0; m < ENV_MIPS; m++) {
      const n = Math.max(1, ENV_W >> m) * Math.max(1, ENV_H >> m);
      for (let i = 0; i < n; i++) {
        u16[o++] = h[0]!; u16[o++] = h[1]!; u16[o++] = h[2]!; u16[o++] = 0x3c00;
      }
    }
  }
  return bin;
}

/** Half-float bits for a non-negative float. Same rule the baker uses. */
function halfBits(v: number): number {
  if (!(v > 0)) return 0;
  if (v < 6.104e-5) return 0;
  const e = Math.min(30, Math.max(1, Math.floor(Math.log2(v)) + 15));
  const m = Math.round((v / Math.pow(2, e - 15) - 1) * 1024);
  return (e << 10) | Math.min(1023, Math.max(0, m));
}
